import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import SharedFooter from "@/components/SharedFooter";
import { 
  Camera, Upload, Send, X, Loader2, 
  HelpCircle, BookOpen, Lightbulb, MessageSquare,
  ChevronRight, ArrowLeft, Sparkles, Home,
  Volume2, VolumeX, Music, Music2, AlertTriangle, LogOut
} from "lucide-react";
import { 
  toggleSound,
  toggleMusic,
  stopMusic
} from "@/utils/soundEffects";

const SUBJECTS = [
  { id: "mathematics", name: "Mathematics", emoji: "🔢" },
  { id: "science", name: "Science", emoji: "🔬" },
  { id: "english", name: "English", emoji: "📝" },
  { id: "hindi", name: "Hindi", emoji: "🇮🇳" }
];

export default function HomeworkHelper({ 
  childId, 
  onBack, 
  studentName = "Student",
  grade = 7 
}) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  
  // Sound state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);
  
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const handleToggleSound = () => {
    const newState = toggleSound();
    setSoundEnabled(newState);
  };

  const handleToggleMusic = () => {
    const newState = toggleMusic();
    setMusicEnabled(newState);
  };

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowBackConfirm(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/");
    } catch (error) {
      navigate("/");
    }
  };

  // Cleanup music on unmount
  useEffect(() => {
    return () => {
      stopMusic();
    };
  }, []);

  // Start background music by default
  useEffect(() => {
    if (musicEnabled) {
      toggleMusic(true);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch credit balance
  useEffect(() => {
    fetchBalance();
  }, [childId]);

  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API}/homework/balance/${childId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      }
    } catch (error) {
      console.error("Failed to fetch balance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("Image too large. Please use an image under 10MB.");
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const sendQuestion = async () => {
    if (!inputValue.trim() && !selectedImage) {
      toast.error("Please type a question or upload an image");
      return;
    }

    setSending(true);

    try {
      let imageBase64 = null;
      if (selectedImage) {
        // Convert to base64
        const reader = new FileReader();
        imageBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const base64 = reader.result.split(",")[1];
            resolve(base64);
          };
          reader.readAsDataURL(selectedImage);
        });
      }

      // Add user message to chat
      const userMessage = {
        id: Date.now(),
        role: "student",
        content: inputValue || "📷 Image question",
        image: imagePreview,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      
      // Clear inputs
      const questionText = inputValue;
      setInputValue("");
      removeImage();

      // Call API
      const endpoint = conversationId ? "/homework/follow-up" : "/homework/help";
      const payload = conversationId 
        ? { conversation_id: conversationId, child_id: childId, message: questionText }
        : { child_id: childId, question_text: questionText, image_base64: imageBase64, language: "english" };

      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        if (response.status === 402) {
          toast.error("Credits exhausted! Ask your parent to recharge.");
          return;
        }
        throw new Error(error.detail?.message || error.detail || "Failed to get help");
      }

      const data = await response.json();
      
      // Set conversation ID for follow-ups
      if (!conversationId) {
        setConversationId(data.conversation_id);
      }

      // Add tutor response
      const tutorMessage = {
        id: Date.now() + 1,
        role: "tutor",
        content: data.response,
        hints: data.hints || [],
        subject: data.subject_detected,
        isFollowUp: data.is_follow_up,
        creditsUsed: data.credits_used,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, tutorMessage]);

      // Update balance
      if (data.credits_remaining !== undefined) {
        setBalance(prev => prev ? { ...prev, remaining_credits: data.credits_remaining } : prev);
      }

    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Something went wrong");
      // Remove the user message if error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const endConversation = async () => {
    if (conversationId) {
      try {
        await fetch(`${API}/homework/end-conversation/${conversationId}`, {
          method: "POST",
          credentials: "include"
        });
      } catch (e) {
        // Ignore errors
      }
    }
    setConversationId(null);
    setMessages([]);
    toast.success("Conversation ended. Start a new question!");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const canUseHelp = balance && (balance.remaining_credits > 0 || balance.free_trial_remaining > 0);

  // Header right content with credits badge, sound/music toggles, and back button
  // Stacked layout for mobile: audio toggles on top, credits + back button below
  const headerRightContent = (
    <div className="flex flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-2">
      {/* Top row on mobile: Audio toggles only */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggleSound}
          className="rounded-full w-8 h-8 sm:w-9 sm:h-9 bg-gray-100 hover:bg-gray-200"
          data-testid="toggle-sound"
        >
          {soundEnabled ? <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" /> : <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggleMusic}
          className="rounded-full w-8 h-8 sm:w-9 sm:h-9 bg-gray-100 hover:bg-gray-200"
          data-testid="toggle-music"
        >
          {musicEnabled ? <Music className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" /> : <Music2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />}
        </Button>
      </div>
      
      {/* Bottom row on mobile: Credits badge + Back to Profile */}
      <div className="flex items-center gap-2">
        {/* Credits Badge */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full px-2.5 py-1 text-xs sm:text-sm font-medium whitespace-nowrap">
          {balance?.is_free_trial || balance?.free_trial_remaining > 0 ? (
            <span>🎁 {balance?.free_trial_remaining || 0} free</span>
          ) : (
            <span>💳 {balance?.remaining_credits || 0}</span>
          )}
        </div>
        
        {/* Back to Profile */}
        <Button 
          variant="outline"
          size="sm"
          onClick={() => navigate("/student/profile")}
          className="border-purple-500 text-purple-600 hover:bg-purple-50 text-xs sm:text-sm h-7 sm:h-9 px-2 sm:px-3"
          data-testid="homework-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
          Back to Profile
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      {/* Unified Header - no login, no menus, no cart */}
      <UnifiedPortalHeader
        portalType="student"
        userName=""
        userSubtitle=""
        onLogout={onBack}
        showUserInfo={false}
        showHomeButton={false}
        hideLogout={true}
        showNav={false}
        rightContent={headerRightContent}
      />
      
      {/* Homework Helper Title Bar - Centered, larger text, no student info */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Lightbulb className="w-6 h-6" />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold">Homework Helper</h1>
              <p className="text-sm text-purple-200">I&apos;ll guide you, not give answers!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col max-w-4xl w-full mx-auto">
        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Hi {studentName}! 👋
            </h2>
            <p className="text-gray-600 mb-4 max-w-sm">
              I&apos;m your Socratic tutor. I won&apos;t give you direct answers, 
              but I&apos;ll help you understand and solve problems yourself!
            </p>
            
            <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
              <div className="bg-white p-3 rounded-xl shadow-md border border-purple-100">
                <Camera className="w-6 h-6 text-purple-500 mb-2 mx-auto" />
                <p className="text-sm text-gray-600">Take a photo of your homework</p>
              </div>
              <div className="bg-white p-3 rounded-xl shadow-md border border-purple-100">
                <MessageSquare className="w-6 h-6 text-indigo-500 mb-2 mx-auto" />
                <p className="text-sm text-gray-600">Type your question below</p>
              </div>
            </div>

            <div className="mt-6 text-sm text-gray-500">
              <p className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-500" />
                Follow-up questions are FREE!
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "student" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl p-4 ${
                    msg.role === "student"
                      ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-br-none"
                      : "bg-white shadow-md border border-purple-100 rounded-bl-none"
                  }`}
                >
                  {msg.image && (
                    <img 
                      src={msg.image} 
                      alt="Question" 
                      className="max-w-full rounded-lg mb-2 max-h-48 object-contain"
                    />
                  )}
                  <p className={`whitespace-pre-wrap ${msg.role === "tutor" ? "text-gray-800" : ""}`}>
                    {msg.content}
                  </p>
                  
                  {msg.hints && msg.hints.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-purple-100">
                      <p className="text-xs text-purple-600 font-semibold mb-2 flex items-center gap-1">
                        <Lightbulb className="w-3 h-3" /> Thinking Points:
                      </p>
                      <ul className="space-y-1">
                        {msg.hints.slice(0, 3).map((hint, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <ChevronRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            {hint}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {msg.role === "tutor" && (
                    <p className="text-xs text-gray-400 mt-2">
                      {msg.isFollowUp ? "✨ Follow-up (free)" : `📚 ${msg.subject || "General"}`}
                    </p>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-gray-200">
        {!canUseHelp ? (
          <div className="text-center p-4 bg-red-50 rounded-xl">
            <p className="text-red-600 font-medium">Credits exhausted!</p>
            <p className="text-sm text-gray-600 mt-1">Ask your parent to recharge your Homework Helper credits.</p>
          </div>
        ) : (
          <>
            {imagePreview && (
              <div className="relative inline-block mb-3">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="max-h-32 rounded-lg border-2 border-purple-200"
                />
                <button
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <div className="flex items-end gap-2">
              {/* Camera Button */}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={cameraInputRef}
                onChange={handleImageSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => cameraInputRef.current?.click()}
                className="flex-shrink-0 border-purple-200 hover:bg-purple-50"
                disabled={sending}
                data-testid="homework-camera-btn"
              >
                <Camera className="w-5 h-5 text-purple-500" />
              </Button>

              {/* Upload Button */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 border-purple-200 hover:bg-purple-50"
                disabled={sending}
                data-testid="homework-upload-btn"
              >
                <Upload className="w-5 h-5 text-purple-500" />
              </Button>

              {/* Text Input */}
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question or send a photo..."
                className="flex-1 resize-none rounded-xl border-2 border-purple-200 p-3 focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none min-h-[48px] max-h-32"
                rows={1}
                disabled={sending}
                data-testid="homework-input"
              />

              {/* Send Button */}
              <Button
                onClick={sendQuestion}
                disabled={sending || (!inputValue.trim() && !selectedImage)}
                className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                data-testid="homework-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>

            {/* End Conversation Button */}
            {conversationId && (
              <div className="mt-3 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={endConversation}
                  className="text-gray-500 hover:text-purple-600"
                  data-testid="homework-end-btn"
                >
                  Done with this question? Start new →
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      </main>
      
      {/* Back Button Confirmation Dialog */}
      <Dialog open={showBackConfirm} onOpenChange={setShowBackConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
            <DialogTitle className="text-center">Leave Homework Helper?</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to leave? You&apos;ll be logged out of your session.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              onClick={() => setShowBackConfirm(false)}
              className="sm:w-auto"
            >
              Stay Here
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white sm:w-auto"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Shared Footer */}
      <SharedFooter />
    </div>
  );
}
